/**
 * Container Breakpoints Mixin
 *
 * Provides container-based responsive breakpoints that work with split panes
 * and other container-constrained layouts, unlike Vuetify's viewport-based
 * breakpoints which only respond to overall window size.
 *
 * Usage:
 * ```
 * import containerBreakpoints from '@/mixins/containerBreakpoints.js'
 *
 * export default {
 *   mixins: [containerBreakpoints],
 *   mounted() {
 *     this.initContainerBreakpoints(this.$el); // or specific container element
 *   }
 * }
 * ```
 *
 * Provides computed properties:
 * - $containerBreakpoint.xs, sm, md, lg, xl (boolean)
 * - $containerBreakpoint.smAndUp, mdAndUp, etc.
 * - $containerBreakpoint.name ('xs', 'sm', 'md', 'lg', 'xl')
 * - $containerBreakpoint.width (current container width)
 */

export default {
  data() {
    return {
      containerWidth: 0,
      containerResizeObserver: null,
      // Cache the breakpoint name to avoid unnecessary re-renders
      _cachedBreakpointName: "xs",
      // Cache the entire breakpoint object to prevent new object creation on every access
      _cachedBreakpointObject: null,
    };
  },
  computed: {
    // Compute breakpoint name separately to detect actual changes
    _containerBreakpointName() {
      const width = this.containerWidth;

      // Vuetify's default breakpoints
      const breakpoints = {
        xs: 600,
        sm: 960,
        md: 1264,
        lg: 1904,
      };

      // Determine current breakpoint
      if (width >= breakpoints.lg) return "xl";
      if (width >= breakpoints.md) return "lg";
      if (width >= breakpoints.sm) return "md";
      if (width >= breakpoints.xs) return "sm";
      return "xs";
    },
    $containerBreakpoint() {
      const currentBreakpoint = this._containerBreakpointName;

      // Return cached object if breakpoint hasn't changed (prevents unnecessary re-renders)
      if (
        this._cachedBreakpointObject &&
        this._cachedBreakpointObject.name === currentBreakpoint
      ) {
        return this._cachedBreakpointObject;
      }

      // Create new breakpoint object only when breakpoint actually changes
      const breakpointObject = Object.freeze({
        name: currentBreakpoint,

        // Individual breakpoint checks
        xs: currentBreakpoint === "xs",
        sm: currentBreakpoint === "sm",
        md: currentBreakpoint === "md",
        lg: currentBreakpoint === "lg",
        xl: currentBreakpoint === "xl",

        // Range checks (andUp) - based on breakpoint name, not raw width
        xsAndUp: true,
        smAndUp: currentBreakpoint !== "xs",
        mdAndUp: ["md", "lg", "xl"].includes(currentBreakpoint),
        lgAndUp: ["lg", "xl"].includes(currentBreakpoint),
        xlAndUp: currentBreakpoint === "xl",

        // Range checks (andDown) - based on breakpoint name, not raw width
        xsAndDown: currentBreakpoint === "xs",
        smAndDown: ["xs", "sm"].includes(currentBreakpoint),
        mdAndDown: ["xs", "sm", "md"].includes(currentBreakpoint),
        lgAndDown: ["xs", "sm", "md", "lg"].includes(currentBreakpoint),
        xlAndDown: true,

        // Range checks (only)
        xsOnly: currentBreakpoint === "xs",
        smOnly: currentBreakpoint === "sm",
        mdOnly: currentBreakpoint === "md",
        lgOnly: currentBreakpoint === "lg",
        xlOnly: currentBreakpoint === "xl",
      });

      // Cache for next access
      this._cachedBreakpointObject = breakpointObject;
      return breakpointObject;
    },
  },
  methods: {
    /**
     * Get breakpoint name for a given width
     * @param {number} width - The container width
     * @returns {string} The breakpoint name
     */
    _getBreakpointForWidth(width) {
      const breakpoints = { xs: 600, sm: 960, md: 1264, lg: 1904 };
      if (width >= breakpoints.lg) return "xl";
      if (width >= breakpoints.md) return "lg";
      if (width >= breakpoints.sm) return "md";
      if (width >= breakpoints.xs) return "sm";
      return "xs";
    },

    /**
     * Initialize container breakpoint monitoring
     * @param {Element} element - The container element to monitor
     */
    initContainerBreakpoints(element) {
      if (!element) {
        console.warn("Container breakpoints: No element provided");
        return;
      }

      // Clean up existing observer
      this.destroyContainerBreakpoints();

      // Set initial width
      this.containerWidth = element.offsetWidth;
      this._cachedBreakpointName = this._getBreakpointForWidth(
        this.containerWidth
      );

      // Set up ResizeObserver to monitor container size changes
      if ("ResizeObserver" in window) {
        this.containerResizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            // Use borderBoxSize if available (more accurate)
            let newWidth;
            if (entry.borderBoxSize && entry.borderBoxSize.length > 0) {
              newWidth = entry.borderBoxSize[0].inlineSize;
            } else {
              // Fallback to contentRect
              newWidth = entry.contentRect.width;
            }

            // Only update if the breakpoint would change (to prevent unnecessary re-renders)
            const newBreakpoint = this._getBreakpointForWidth(newWidth);
            if (newBreakpoint !== this._cachedBreakpointName) {
              this._cachedBreakpointName = newBreakpoint;
              this.containerWidth = newWidth;
            }
          }
        });

        this.containerResizeObserver.observe(element);
      } else {
        // Fallback for browsers without ResizeObserver support
        console.warn(
          "ResizeObserver not supported - container breakpoints will not update on resize"
        );
      }
    },

    /**
     * Clean up container breakpoint monitoring
     */
    destroyContainerBreakpoints() {
      if (this.containerResizeObserver) {
        this.containerResizeObserver.disconnect();
        this.containerResizeObserver = null;
      }
    },
  },

  beforeDestroy() {
    // Clean up observer when component is destroyed
    this.destroyContainerBreakpoints();
  },
};
